import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Package } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/features/admin/api";
import { formatApiError } from "@/features/auth/formatError";
import { Input } from "@/components/ui/input";
import { ROUTES } from "@/constants/routes";

export default function AdminInventory() {
    const qc = useQueryClient();
    const [filter, setFilter] = useState<"low" | "out" | "">("low");

    const { data, isLoading } = useQuery({
        queryKey: ["admin", "inventory", filter],
        queryFn: () =>
            adminApi.listProducts({
                stock: filter || undefined,
                page_size: 100,
            }),
    });

    // Local state for stock input drafts.
    const [drafts, setDrafts] = useState<Record<string, number>>({});

    const updateMut = useMutation({
        mutationFn: async ({ id, stock_count }: { id: string; stock_count: number }) => {
            const p = await adminApi.getProduct(id);
            return adminApi.updateProduct(id, {
                ...p,
                stock_count,
                images: p.images,
                tone: p.tone,
                variants: p.variants,
                ingredients: p.ingredients,
            });
        },
        onSuccess: (p) => {
            qc.invalidateQueries({ queryKey: ["admin", "inventory"] });
            qc.invalidateQueries({ queryKey: ["admin", "products"] });
            setDrafts((d) => {
                const next = { ...d };
                delete next[p.id];
                return next;
            });
            toast.success(`${p.name}: stock set to ${p.stock_count}`);
        },
        onError: (err) => toast.error(formatApiError(err, "Stock update failed")),
    });

    return (
        <div data-testid="admin-inventory-page" className="space-y-5">
            <div>
                <h1 className="text-2xl font-extrabold sm:text-3xl">Inventory</h1>
                <p className="text-sm text-muted-foreground">
                    Update stock quantities directly. Stock auto-decrements when orders are placed.
                </p>
            </div>

            <div className="flex flex-wrap gap-2">
                {[
                    { v: "low", l: "Low stock" },
                    { v: "out", l: "Out of stock" },
                    { v: "", l: "All products" },
                ].map((f) => (
                    <button
                        key={f.v || "all"}
                        onClick={() => setFilter(f.v as "low" | "out" | "")}
                        data-testid={`admin-inventory-tab-${f.v || "all"}`}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                            filter === f.v ? "bg-ink text-white" : "border border-border hover:bg-muted"
                        }`}
                    >
                        {f.l}
                    </button>
                ))}
            </div>

            <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card">
                <table className="min-w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Product</th>
                            <th className="px-4 py-3">Category</th>
                            <th className="px-4 py-3">Current stock</th>
                            <th className="px-4 py-3">Low-stock at</th>
                            <th className="px-4 py-3 text-right">Set stock</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                        {isLoading ? (
                            <tr>
                                <td colSpan={5} className="p-6 text-center text-sm text-muted-foreground">
                                    Loading inventory…
                                </td>
                            </tr>
                        ) : data && data.items.length > 0 ? (
                            data.items.map((p) => {
                                const draft = drafts[p.id] ?? p.stock_count;
                                const isLow =
                                    p.stock_count > 0 && p.stock_count <= p.low_stock_threshold;
                                const isOut = p.stock_count <= 0;
                                return (
                                    <tr key={p.id} data-testid={`admin-inventory-row-${p.id}`}>
                                        <td className="px-4 py-3">
                                            <Link
                                                to={ROUTES.adminProductEditBuild(p.id)}
                                                className="font-semibold hover:underline"
                                            >
                                                {p.name}
                                            </Link>
                                            <p className="text-[11px] text-muted-foreground">{p.brand} · {p.weight}</p>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">
                                            {p.category_name}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${
                                                    isOut
                                                        ? "bg-rose-500/10 text-rose-700"
                                                        : isLow
                                                          ? "bg-amber-500/10 text-amber-700"
                                                          : "bg-emerald-500/10 text-emerald-700"
                                                }`}
                                            >
                                                {isOut || isLow ? (
                                                    <AlertTriangle className="h-3 w-3" />
                                                ) : (
                                                    <Package className="h-3 w-3" />
                                                )}
                                                {p.stock_count}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-xs">{p.low_stock_threshold}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-2">
                                                <Input
                                                    type="number"
                                                    min={0}
                                                    className="h-8 w-24 text-right"
                                                    value={draft}
                                                    onChange={(e) =>
                                                        setDrafts((d) => ({ ...d, [p.id]: Number(e.target.value) }))
                                                    }
                                                    data-testid={`admin-inventory-input-${p.id}`}
                                                />
                                                <button
                                                    type="button"
                                                    disabled={draft === p.stock_count || updateMut.isPending}
                                                    onClick={() => updateMut.mutate({ id: p.id, stock_count: draft })}
                                                    data-testid={`admin-inventory-save-${p.id}`}
                                                    className="rounded-full bg-brand px-3 py-1 text-xs font-bold text-white disabled:opacity-50"
                                                >
                                                    Save
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={5} className="p-6 text-center text-sm text-muted-foreground">
                                    Nothing to show here — you're fully stocked!
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
